import i18n from 'i18next';

import { getIsLoadingServiceByParams, getTravellersMap } from '../../../utils';
import {
	AeroexpressSelectedTrip,
	CheckinPassengerFragment,
	CheckinSegmentFragment,
	CheckoutOrderFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	GdsServiceVipSubType,
	IncludedGdsServicesFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderAdditionalServicesFragment,
	PassengerFragment,
	PassengerSelectedGdsServicesProductFragment,
	SegmentFragment
} from '@websky/graphql';
import { AggregatedSummaryServices, SelectedSummaryService, ServiceSummaryType } from '../model/types';
import { SelectedServicesState } from '../../../Checkout/store/selectedServices/reducers';
import { LoadingServicesParamsState, LoadingServiceType } from '../../../Checkout/store/loadingServicesParams/reducers';
import { CartGroupOptionAdapterType } from '../../../Cart/CartItemOptionAdapter/CartItemOptionAdapter';
import { ServiceOptionItem } from '../../../ServiceSummary/types';
import { Config } from '../../../context';

import { getActualProduct } from '../../../ServiceSummary/utils';
import { getAeroexpressName } from './getAeroexpressName';
import { getRelatedServiceType } from './getRelatedServiceType';
import { includedOptionItemFactory } from './includedOptionItemFactory';
import { additionalOptionItemFactory } from './additionalOptionItemFactory';
import { optionGroupsFactory } from './optionGroupsFactory';
import { ALL_SEGMENTS_KEY } from '@shared/lib/flight';
import {
	buildGdsServicesById,
	buildSelectedGdsServiceKeys,
	buildSelectedServiceKey,
	filterSegmentIdsByReplacedBy
} from '@shared/lib/gdsService';

export function getAggregatedSummaryServices(
	config: Config,
	additionalServices: OrderAdditionalServicesFragment,
	segments: (SegmentFragment | CheckinSegmentFragment)[],
	travellers: (PassengerFragment | CheckinPassengerFragment)[],
	selectedServices: SelectedServicesState,
	segmentsGroupsCount: number,
	isConfirmed: boolean,
	priceToPay: CheckoutOrderFragment['priceToPay'],
	loadingServicesParams: LoadingServicesParamsState = {
		id: [],
		rfisc: [],
		subType: [],
		type: []
	}
): AggregatedSummaryServices {
	const gdsServices = additionalServices.gdsServices?.services ?? [];

	const travellersMap = getTravellersMap(travellers);
	const gdsServicesById = buildGdsServicesById(gdsServices);
	const selectedBaggageKeys = buildSelectedGdsServiceKeys(travellers, gdsServicesById);
	const servicesProductsMap = new Map<string, PassengerSelectedGdsServicesProductFragment[]>();
	const segmentsCount = segments.length;

	const summaryItemsByTypes: AggregatedSummaryServices = {
		Seat: [],
		Baggage: [],
		BaggageDelivery: [],
		Animal: [],
		Meal: [],
		BusinessLounge: [],
		PriorityBoarding: [],
		RefundableTicket: [],
		Insurance: [],
		Aeroexpress: [],
		Transfer: [],
		Loyalty: [],
		CapsuleHotel: [],
		Upgrade: [],
		Sms: [],
		Misc: []
	};

	// Insurance services
	if (additionalServices.insurances?.selectedInsurances?.length) {
		const availableInsuranceCodes = new Set<string>(
			additionalServices.insurances?.insurancePrograms?.map(insurance => insurance.code) ?? []
		);

		const isLoading = getIsLoadingServiceByParams(loadingServicesParams, { type: LoadingServiceType.Insurance }, [
			'type'
		]);

		const items: ServiceOptionItem[] = [];

		additionalServices.insurances.selectedInsurances.forEach(selectedInsurance => {
			const insuranceCode = selectedInsurance.insuranceProgram.code;

			if (availableInsuranceCodes.has(insuranceCode)) {
				const included = !!selectedInsurance.policyUrl || (isConfirmed && !priceToPay?.insurance?.amount);

				items.push({
					id: insuranceCode,
					name: selectedInsurance.insuranceProgram.name,
					passengerName: selectedInsurance.passengerName,
					price: selectedInsurance.insuranceProgram.totalPrice,
					passengerPrice: selectedInsurance.passengerPrice,
					downloadUrl: included ? selectedInsurance.policyUrl : null,
					allPassengersDownloadUrls: additionalServices.insurances.selectedInsurances
						.filter(
							insurance => insurance.insuranceProgram?.code === insuranceCode && !!insurance.policyUrl
						)
						.map(insurance => insurance.policyUrl),
					deleteMeta: !included ? { code: insuranceCode } : null,
					included,
					isLoading
				});
			}
		});

		if (items.length) {
			summaryItemsByTypes.Insurance.push({ items });
		}
	}

	// Transfer services
	if (additionalServices.transfer?.aeroexpress?.selectedTrips?.length) {
		const isLoading = getIsLoadingServiceByParams(loadingServicesParams, { type: LoadingServiceType.Transfer }, [
			'type'
		]);

		additionalServices.transfer.aeroexpress.selectedTrips.forEach(selectedTrip => {
			summaryItemsByTypes.Aeroexpress.push({
				header: i18n.t('All passengers', { ns: 'Checkout' }),
				items: [
					{
						id: selectedTrip.id,
						name: getAeroexpressName(selectedTrip as AeroexpressSelectedTrip),
						segmentIds: selectedTrip.segments?.map(segment => segment.id) ?? [],
						price: selectedTrip.price,
						deleteMeta: { tripId: selectedTrip.id },
						isLoading
					}
				]
			});
		});
	}

	const optionsItemsMap: {
		[travellerId: string]: {
			[segmentKey: string]: {
				[gdsServiceType in ServiceSummaryType]?: ServiceOptionItem[];
			};
		};
	} = {};

	// Included services
	travellers.forEach(traveller => {
		const travellerId = traveller.id;

		if (!optionsItemsMap[travellerId]) {
			optionsItemsMap[travellerId] = {};
		}

		traveller.services?.gdsServices?.services?.forEach(service => {
			const key = buildSelectedServiceKey(service.serviceId, service.segmentIds);

			servicesProductsMap.set(key, [...(servicesProductsMap.get(key) ?? []), ...service.products]);
		});

		const includedServicesMap = new Map<string, Map<string, IncludedGdsServicesFragment>>();

		traveller.services?.brandIncludedServices?.services.forEach(includedService => {
			let segmentIds = includedService.segmentIds;

			/* WEBSKY-8550: Скрывает включенный багаж для которого выбран апгрейд из `isReplacedBy`. */
			if (includedService.service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage) {
				const baggageService = includedService.service;

				segmentIds = filterSegmentIdsByReplacedBy(
					segmentIds,
					travellerId,
					baggageService.isReplacedBy,
					selectedBaggageKeys
				);

				if (!segmentIds.length) {
					return;
				}
			}

			const key = `${includedService.service?.rfisc ?? includedService.serviceId}/${includedService.count}`;
			const includedServicesBySegment = includedServicesMap.get(key);

			segmentIds.forEach(segmentId => {
				if (includedServicesBySegment) {
					includedServicesBySegment.set(segmentId, includedService);
				} else {
					includedServicesMap.set(key, new Map([[segmentId, includedService]]));
				}
			});
		});

		includedServicesMap.forEach(includedServices => {
			const segmentIds = [...includedServices.keys()];
			const isAllSegments = segmentIds.length === segmentsCount;

			(isAllSegments ? [ALL_SEGMENTS_KEY] : segmentIds).map(key => {
				const includedService = includedServices.get(key) ?? [...includedServices.values()][0];

				const type = includedService.service.type;
				const gdsServiceType = getRelatedServiceType(type);

				const productsKey = buildSelectedServiceKey(includedService.serviceId, includedService.segmentIds);
				const products = servicesProductsMap.get(productsKey);

				const includedItem = includedOptionItemFactory({
					key,
					travellerId,
					segmentsGroupsCount,
					segments,
					products,
					includedService
				});

				if (includedItem) {
					if (!optionsItemsMap[travellerId][key]) {
						optionsItemsMap[travellerId][key] = {};
					}

					if (!optionsItemsMap[travellerId][key][gdsServiceType]) {
						optionsItemsMap[travellerId][key][gdsServiceType] = [];
					}

					optionsItemsMap[travellerId][key][gdsServiceType].push(includedItem);
				}
			});
		});
	});

	// Selected services
	const selectedServicesMap = new Map<string, Map<string, SelectedSummaryService>>();

	for (const travellerId in selectedServices) {
		if (!optionsItemsMap[travellerId]) {
			optionsItemsMap[travellerId] = {};
		}

		for (const serviceId in selectedServices[travellerId]) {
			const services = selectedServices[travellerId][serviceId];

			if (!services) {
				continue;
			}

			const gdsService = gdsServicesById[serviceId];

			const isRefundableTicketService =
				(gdsService as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment)?.subType ===
				GdsServiceVipSubType.RefundableTicket;

			services.forEach(service => {
				const serviceRfiscOrId = gdsService?.rfisc ?? serviceId;
				const keyValues = [serviceRfiscOrId, travellerId];

				const isSeatType = service.servicePayload.type === OrderAdditionalServiceGdsServiceServiceType.Seat;
				const separateBySegment = isSeatType;

				if (separateBySegment) {
					const segmentId = service.segments[0];

					keyValues.push(segmentId);
				} else if (!isRefundableTicketService) {
					let count = service.number;

					if (
						'confirmedCount' in service.servicePayload &&
						typeof service.servicePayload.confirmedCount === 'number'
					) {
						count = service.servicePayload.confirmedCount;
					}

					keyValues.push(count.toString());
				}

				const selectedServiceProducts =
					servicesProductsMap.get(buildSelectedServiceKey(service.servicePayload.id, service.segments)) ?? [];
				const productStatus = getActualProduct(selectedServiceProducts)?.status ?? '';

				keyValues.push(productStatus);

				const key = keyValues.join('/');
				const selectedService: SelectedSummaryService = { ...service, travellerId, gdsService };

				service.segments.map(segmentId => {
					const selectedServicesBySegment = selectedServicesMap.get(key);

					if (selectedServicesBySegment) {
						selectedServicesBySegment.set(segmentId, selectedService);
					} else {
						selectedServicesMap.set(key, new Map([[segmentId, selectedService]]));
					}
				});
			});
		}
	}

	selectedServicesMap.forEach(selectedServices => {
		const segmentIds = [...selectedServices.keys()];
		const selectedSegmentsCount = segmentIds.length;

		const isAllSegments = selectedSegmentsCount === segmentsCount;

		const serviceProductsMap = new Map<string, PassengerSelectedGdsServicesProductFragment>();
		[...selectedServices.values()].forEach(service => {
			const key = buildSelectedServiceKey(service.servicePayload.id, service.segments);
			const products = servicesProductsMap.get(key) ?? [];

			products.forEach(product => {
				serviceProductsMap.set(product.id, product);
			});
		});

		const serviceProducts = [...serviceProductsMap.values()];

		(isAllSegments ? [ALL_SEGMENTS_KEY] : segmentIds).forEach(key => {
			const selectedService = selectedServices.get(key) ?? [...selectedServices.values()][0];

			const serviceId = selectedService.servicePayload.id;
			const travellerId = selectedService.travellerId;
			const gdsService = selectedService.gdsService;

			const type = selectedService.servicePayload?.type ?? gdsService?.type;
			const subType =
				(gdsService as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment)?.baggageType ??
				(gdsService as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment)?.subType;

			const gdsServiceType = getRelatedServiceType(type, subType, {
				isAnimalAvailable: config.global?.servicesAvailable?.animal
			});

			const isLoading = getIsLoadingServiceByParams(loadingServicesParams, { id: serviceId, type, subType });

			const additionalItem = additionalOptionItemFactory({
				key,
				travellerId,
				gdsServiceType,
				segmentsGroupsCount,
				segments,
				isLoading,
				selectedService,
				selectedSegmentsCount,
				products: serviceProducts,
				gdsServiceRfisc: gdsService?.rfisc
			});

			if (!!additionalItem) {
				if (!optionsItemsMap[travellerId][key]) {
					optionsItemsMap[travellerId][key] = {};
				}

				if (!optionsItemsMap[travellerId][key][gdsServiceType]) {
					optionsItemsMap[travellerId][key][gdsServiceType] = [];
				}

				optionsItemsMap[travellerId][key][gdsServiceType].push(
					...(Array.isArray(additionalItem) ? additionalItem : [additionalItem])
				);
			}
		});
	});

	const optionItemsByKey = new Map<string, Map<ServiceSummaryType, CartGroupOptionAdapterType>>();

	for (const travellerId in optionsItemsMap) {
		for (const segmentKey in optionsItemsMap[travellerId]) {
			for (const type in optionsItemsMap[travellerId][segmentKey]) {
				const gdsServiceType = type as ServiceSummaryType;
				const items = optionsItemsMap[travellerId][segmentKey][gdsServiceType];

				if (!items?.length) {
					continue;
				}

				const optionGroup = optionGroupsFactory({
					items,
					gdsServiceType,
					segments,
					key: segmentKey,
					traveller: travellersMap.get(travellerId)
				});

				if (
					!Array.isArray(optionGroup) &&
					(gdsServiceType === 'Seat' || gdsServiceType === 'Meal' || gdsServiceType === 'Sms')
				) {
					let key = travellerId;

					if (gdsServiceType === 'Sms') {
						key = `${optionGroup.items[0].id}/${gdsServiceType}`;
					}

					const optionItemsByServiceKey = optionItemsByKey.get(key);

					if (optionItemsByServiceKey) {
						const gdsServiceOptionItems = optionItemsByServiceKey.get(gdsServiceType);

						if (gdsServiceOptionItems) {
							gdsServiceOptionItems.items.push(...optionGroup.items);
						} else {
							optionItemsByServiceKey.set(gdsServiceType, optionGroup);
						}
					} else {
						optionItemsByKey.set(travellerId, new Map([[gdsServiceType, optionGroup]]));
					}

					continue;
				}

				summaryItemsByTypes[gdsServiceType].push(...(Array.isArray(optionGroup) ? optionGroup : [optionGroup]));
			}
		}
	}

	if (optionItemsByKey.size) {
		optionItemsByKey.forEach(optionItemsByGdsServiceType => {
			optionItemsByGdsServiceType.forEach((group, gdsServiceType) => {
				group.items.sort((a, b) => {
					const aPriority = (a.included ? 1 : 0) + (a.segment === ALL_SEGMENTS_KEY ? 1 : 0);
					const bPriority = (b.included ? 1 : 0) + (b.segment === ALL_SEGMENTS_KEY ? 1 : 0);

					return bPriority - aPriority;
				});

				summaryItemsByTypes[gdsServiceType].push(group);
			});
		});
	}

	return summaryItemsByTypes;
}
